/**
 * Personalize Form Store
 *
 * This store manages personalize form data in memory, derived from the details
 * of the current `designProject` from the design project store.
 *
 * Key Features:
 * - Parse design product current config and derive
 *   - Designer-level color options and choices
 *   - Fields for PartElements, organized based on the form parts from `data/personalize`
 *   - Available printing process options
 */

import * as changeCase from 'change-case'
import {
    type FieldsetConfig,
    type FieldType,
    type FormPartType,
    personalizeFormPartConfigs,
} from '@/data/personalize'
import type {
    Color,
    Font,
    Print,
    Monogram,
    Liner,
    Motif,
} from '~~/layers/base/types/productPartConfiguration'
import type {
    ConfigurationTemplate,
    PartElement,
    ProductPart,
} from '~~/layers/base/types/designProject'
import { surfaceIds } from '@/services/util.service'
import {
    initializeHighLevelData,
    sortPrintingProcessesByPreferred,
} from '@/utils/initializeHighLevelData'
import { getPrintingProcessShortName } from '@/utils/printingProcess'

export interface ParsedFont extends Font {
    defaultValue: number
    fontMin: number
    fontMax: number
}

export interface FormFieldConfig {
    id: string
    label: string
    type: FieldType
    required: boolean
    fieldsetPath?: string
    options?: Exclude<PartElement['values'], null>
    placeholder?: string
    font?: ParsedFont
    fontOptions?: ParsedFont[]
    aspectRatio?: number
    /** The width of the digital photo, in inches */
    width?: number
    /** The height of the digital photo, in inches */
    height?: number
    /** The maximum number of lines for text-based fields */
    maxLines?: number
}

interface FormDataEntry {
    value: PartElement['value']
    elementName: string | undefined
    color: number
    font: ParsedFont | undefined
}

export const usePersonalizeFormStore = defineStore('personalizeForm', () => {
    const ELEMENT_TYPES_WITH_FIELDS = ['text', 'liner', 'motif', 'monogram', 'image']

    /**
     * Map of fieldsets that require explicit user selection to show/hide.
     * Fieldsets listed here will have a visibility toggle in the UI and will be
     * marked as removed by default unless explicitly set to 'show'.
     */
    const FIELDSETS_REQUIRING_VISIBILITY_TOGGLE = new Set(['returnAddress'])

    /**
     * Determines if a fieldset requires explicit user selection to show/hide.
     *
     * @param fieldsetId - The fieldset ID (e.g., 'returnAddress', 'text', 'motif')
     * @returns True if the fieldset requires explicit show/hide toggle
     */
    const fieldsetRequiresVisibilityToggle = (fieldsetId: string): boolean => {
        return FIELDSETS_REQUIRING_VISIBILITY_TOGGLE.has(fieldsetId)
    }

    const designProjectStore = useDesignProjectStore()
    const { designProject } = storeToRefs(designProjectStore)
    const { updateDesignProjectConfigurationCurrent } = designProjectStore

    /** The currently selected printing process for the design project */
    const selectedPrintingProcess = ref<Print | null>(null)

    /**
     * The currently selected color cids for the entire design project.
     * Colors assigned to individual fields will be indexes of this list.
     */
    const selectedColors = ref<string[]>([])

    /**
     * A map of the PartElement names to their corresponding field keys.
     */
    const fieldKeyMap = ref<Record<string, string>>({})

    /**
     * The currently selected colors for the entire design project
     * as full objects instead of cids.
     * Each color includes an `inUse` property indicating whether any field
     * in `formFieldColors` is using that color index.
     */
    const selectedColorOptions = computed(() => {
        // Get the set of color indexes that are currently in use by visible (non-hidden) fields
        const usedColorIndexes = new Set(
            Object.entries(formFieldColors.value)
                .filter(([fieldKey]) => !formFieldHidden.value[changeCase.camelCase(fieldKey)])
                .map(([, colorIndex]) => colorIndex)
        )

        return selectedColors.value
            .map((id) => colorOptions.value.find((color) => id === color.cid))
            .filter((color) => !!color)
            .map((color, index) => ({
                ...color,
                inUse: usedColorIndexes.has(index),
            }))
    })

    /**
     * The product parts of the design project.
     *
     * TODO: need to sort these to prioritize non-envelopes, since the envelope is
     * not always second in the list of parts
     */
    const productParts = computed(() => {
        const isEnvelope = (part: ProductPart) => part.name.includes('Envelope')
        const hasImageField = (part: ProductPart) => part.elements.find((el) => el.type === 'image')

        return (
            (designProject.value?.configurationCurrent.baseStructure as ProductPart[]) || []
        ).sort((a, b) => {
            // Sort parts with "Envelope" in the name later in the list
            if (isEnvelope(a) && !isEnvelope(b)) {
                return 1
            }
            if (!isEnvelope(a) && isEnvelope(b)) {
                return -1
            }

            // Sort parts with image fields earlier in the list
            if (hasImageField(a) && !hasImageField(b)) {
                return 1
            }
            if (!hasImageField(a) && hasImageField(b)) {
                return -1
            }

            // Fallback to comparing the names of the parts
            return a.name.localeCompare(b.name)
        })
    })

    /**
     * A two-dimensional array of PartElements for the design project grouped by
     * their product part in the base structure.
     */
    const elementsByProductPart = computed(() => productParts.value.map((pp) => pp.elements))

    /** All of the PartElements for the design project in a single, flat array. */
    const allDesignProjectElements = computed(() => elementsByProductPart.value.flat() || [])

    /** Map of fields values by their field ids. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formFieldValues = ref<{ [key: string]: any }>({})

    /**
     * Map of field colors by their field ids. Values correspond to indexes of the
     * `selectedColorOptions` array.
     */
    const formFieldColors = ref<{ [key: string]: number }>({})

    /**
     * Map of field fonts by their field ids. Values are full font objects.
     * Specifically applies to text fields.
     */
    const formFieldFonts = ref<{ [key: string]: ParsedFont | undefined }>({})

    /**
     * Map of field placeholders by their field ids. Only used for text fields.
     */
    const formFieldPlaceholders = ref<{ [key: string]: string }>({})

    /**
     * Map of whether individual form fields are hidden, where the key is the field id
     * and the value is a boolean indicating if the field is hidden.
     */
    const formFieldHidden = ref<{ [fieldId: string]: boolean }>({})

    /**
     * Map of stashed field values, where the key is the field id and the value is
     * either a stashed string value (for general field hiding) or a stashed selection
     * object (Monogram, Liner, Motif for "No X" checkbox hiding).
     */
    const stashedFieldValues = ref<{
        [fieldId: string]: string | Monogram | Liner | Motif
    }>({})

    /**
     * Map of whether form fieldsets are hidden, where the key is the `path`
     * property of the fieldset.
     */
    const fieldsetsHidden = ref<{ [fieldsetPath: string]: 'show' | 'hide' }>({})

    /**
     * Combined form state that merges field values with fieldset visibility selections.
     * Transforms fieldsetsHidden keys to snake_case with _shown suffix for validation.
     * Also includes individual field hiding state for validation purposes.
     */
    const formState = computed(() => ({
        ...formFieldValues.value,
        ...Object.fromEntries(
            Object.entries(fieldsetsHidden.value).map(([key, value]) => [
                `${changeCase.snakeCase(key)}_shown`,
                value,
            ])
        ),
        ...Object.fromEntries(
            Object.entries(formFieldHidden.value).map(([key, value]) => [
                `${changeCase.snakeCase(key)}_hidden`,
                value,
            ])
        ),
    }))

    /**
     * Ref to store whether or not additional colors have been added to this
     * design project before.
     */
    const additionalColorsAdded = ref(false)

    /**
     * A map that zips together the values, colors, and elementName for each field into
     * an object as the value for each field id.
     * TODO: this may not be needed once we're updating the current configuration
     */
    const formData = computed<Record<string, FormDataEntry>>(() =>
        Object.fromEntries(
            Object.entries(formFieldValues.value).map(([key, value]) => [
                key,
                {
                    value,
                    elementName: fieldKeyMap.value[key],
                    color: formFieldColors.value[key] || 0,
                    font: formFieldFonts.value[key],
                },
            ])
        )
    )

    /**
     * Trim leading/trailing whitespace for text field values while preserving
     * raw `formFieldValues` for in-progress typing in the form UI.
     */
    const trimmedFormData = computed<Record<string, FormDataEntry>>(() =>
        Object.fromEntries(
            Object.entries(formData.value).map(([key, data]) => {
                const isTextValue =
                    getFieldTypeByKey(key) === 'text' && typeof data.value === 'string'

                return [
                    key,
                    {
                        ...data,
                        value: isTextValue ? (data.value as string).trim() : data.value,
                    },
                ]
            })
        )
    )

    /** Helper function to get the field key for a PartElement */
    const getFieldKey = (el: PartElement) => changeCase.snakeCase(el.name)

    /**
     * Helper function to set the hidden state for a specific field.
     * Handles value stashing when hiding fields and value restoration when showing fields.
     * @param fieldId - The field ID to update
     * @param hidden - Whether the field should be hidden (true) or visible (false)
     * @param enableValidation - Whether to enable validation on restoration (default: false)
     */
    const setFieldHidden = (
        fieldId: string,
        hidden: boolean,
        _enableValidation: boolean = false
    ) => {
        // formFieldHidden uses camelCase keys, but formFieldValues uses snakeCase keys
        // Convert fieldId to snakeCase for accessing formFieldValues
        const fieldKeySnakeCase = changeCase.snakeCase(fieldId)

        if (hidden) {
            // When hiding a field, stash the current value before clearing it
            const currentValue = formFieldValues.value[fieldKeySnakeCase]
            if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
                stashFieldValue(fieldKeySnakeCase, String(currentValue))
            }
            // Clear the field value
            formFieldValues.value[fieldKeySnakeCase] = undefined
        } else {
            // When showing a field, restore the stashed value if it exists
            const stashedValue = restoreFieldValue(fieldKeySnakeCase)
            if (stashedValue !== undefined) {
                formFieldValues.value[fieldKeySnakeCase] = stashedValue
                // Clear the stashed value after restoration
                clearStashedValue(fieldKeySnakeCase)
                // TODO: Add validation logic here when _enableValidation is true
            }
        }

        // Update the hidden state (fieldId is already in camelCase from Fieldset component)
        formFieldHidden.value[fieldId] = hidden
    }

    /**
     * Helper function to check if a specific field is hidden.
     * @param fieldId - The field ID to check
     * @returns True if the field is hidden, false if visible or not set
     */
    const isFieldHidden = (fieldId: string): boolean => {
        return formFieldHidden.value[fieldId] === true
    }

    /**
     * Helper function to stash a field value when the field is hidden.
     * @param fieldId - The field ID to stash
     * @param value - The value to stash (string for general fields, or Monogram/Liner/Motif for selections)
     */
    const stashFieldValue = (fieldId: string, value: string | Monogram | Liner | Motif) => {
        stashedFieldValues.value[fieldId] = value
    }

    /**
     * Helper function to restore a stashed field value when the field is shown.
     * @param fieldId - The field ID to restore
     * @returns The stashed value if it exists, undefined otherwise
     */
    const restoreFieldValue = (fieldId: string): string | undefined => {
        const value = stashedFieldValues.value[fieldId]
        // Only return if it's a string (for general field values)
        return typeof value === 'string' ? value : undefined
    }

    /**
     * Helper function to restore a stashed selection (Monogram, Liner, or Motif) when showing via "No X" checkbox.
     * @param fieldId - The field ID to restore
     * @returns The stashed selection if it exists, undefined otherwise
     */
    const restoreSelection = (fieldId: string): Monogram | Liner | Motif | undefined => {
        const value = stashedFieldValues.value[fieldId]
        // Only return if it's an object (for selections)
        return typeof value === 'string'
            ? undefined
            : (value as Monogram | Liner | Motif | undefined)
    }

    /**
     * Helper function to clear a stashed value for a specific field.
     * @param fieldId - The field ID to clear the stash for
     */
    const clearStashedValue = (fieldId: string) => {
        const { [fieldId]: _, ...rest } = stashedFieldValues.value
        stashedFieldValues.value = rest
    }

    /**
     * Helper function to clear all stashed values.
     */
    const clearAllStashedValues = () => {
        stashedFieldValues.value = {}
    }

    /**
     * Helper function to stash a selection (Monogram, Liner, or Motif) when hiding via "No X" checkbox.
     * This is a convenience wrapper around stashFieldValue for type safety.
     * @param fieldId - The field ID to stash
     * @param selection - The selection object to stash
     */
    const stashSelection = (fieldId: string, selection: Monogram | Liner | Motif) => {
        stashFieldValue(fieldId, selection)
    }

    /**
     * Helper function to determine if a field change is text-related by checking field type.
     * @param fieldId - The field ID to check
     * @returns True if the field type is 'text', false otherwise
     */
    const isTextField = (fieldId: string): boolean => {
        return getFieldTypeByKey(fieldId) === 'text'
    }

    /**
     * Helper function to categorize watcher triggers based on change type.
     * @param watcherSource - The source of the watcher trigger
     * @returns 'text' for text-related changes, 'non-text' for other changes
     */
    const getChangeType = (watcherSource: string): 'text' | 'non-text' => {
        // Text-related changes come from formData (which includes formFieldValues)
        if (watcherSource === 'formData') {
            return 'text'
        }

        // All other watcher sources are non-text changes
        return 'non-text'
    }

    /**
     * Helper function to detect if changes to formFieldValues are text-related.
     * @param newValues - The new formFieldValues object
     * @param oldValues - The old formFieldValues object
     * @returns 'text' if any changed fields are text fields, 'non-text' otherwise
     */
    const detectFormFieldValuesChangeType = (
        newValues: { [key: string]: unknown },
        oldValues: { [key: string]: unknown }
    ): 'text' | 'non-text' => {
        // Get all field keys that have changed
        const allKeys = new Set([...Object.keys(newValues), ...Object.keys(oldValues)])

        for (const fieldKey of allKeys) {
            const newValue = newValues[fieldKey]
            const oldValue = oldValues[fieldKey]

            // Check if this field's value has changed
            if (newValue !== oldValue) {
                // If this changed field is a text field, return 'text'
                if (isTextField(fieldKey)) {
                    return 'text'
                }
            }
        }

        // If no text fields changed, return 'non-text'
        return 'non-text'
    }

    /**
     * Utility function to map field keys to their field types using existing fieldKeyMap and formParts.
     * @param fieldKey - The field key to look up
     * @returns The field type ('text', 'color', 'motif', etc.) or undefined if not found
     */
    const getFieldTypeByKey = (fieldKey: string): FieldType | undefined => {
        // Find the field in formParts to get its type
        const field = formParts.value
            .flatMap((formPart) => formPart.fieldsets)
            .flatMap((fieldset) => fieldset.fields)
            .find((field) => field.id === fieldKey)

        return field?.type
    }

    /**
     * Sets the current printing process for the designProject based on
     * the provided `processId`. Also triggers a PATCH to persist the
     * selection to the database.
     */
    const setSelectedPrintingProcess = (processId: number) => {
        const printingProcess = printingProcessOptions.value.find(
            (printProcess) => printProcess.processId === processId
        )
        if (!printingProcess) return

        selectedPrintingProcess.value = printingProcess

        // Save changes to the design project
        saveDesignProject()
    }

    /**
     * Parses a Font object into a more usable object by parsing `defaultValue`,
     * `fontMin`, and `fontMax` into integers
     */
    const parseFontObj = (obj: Font | null | undefined) =>
        isNotNullish(obj)
            ? {
                  ...obj,
                  defaultValue: ensureInt(obj.defaultValue),
                  fontMin: ensureInt(obj.fontMin),
                  fontMax: ensureInt(obj.fontMax),
              }
            : undefined

    /**
     * Compute fields for a fieldset based on product elements and configuration.
     * Color fields manually get a color field assigned, since there's (currently) no
     * corresponding Element for a design-level color selection.
     *
     * @param productPart The product part to derive fields from
     * @param fieldset The configuration for the fieldset.
     * @returns An array of computed fields, or an empty array if no matching fields are found.
     */
    const getFieldsForProductPart = (
        productPart: ProductPart,
        fieldset: FieldsetConfig,
        placeholders: Record<string, string | undefined>
    ): FormFieldConfig[] => {
        return fieldset.fieldTypes.length === 1 && fieldset.fieldTypes[0] === 'color'
            ? [
                  {
                      id: 'color',
                      label: 'Color',
                      type: 'color' as FieldType,
                      required: true,
                  },
              ]
            : productPart.elements
                  ?.filter((element) => fieldset.fieldTypes.includes(element.type as FieldType))
                  .map((element) => ({
                      id: getFieldKey(element),
                      label: element.name,
                      type: element.type as FieldType,
                      options: element.values || undefined,
                      required: !element.isOptional,
                      fieldsetPath: fieldset.path,
                      placeholder: placeholders[getFieldKey(element)],
                      // Make sure to unwrap font if wrapped in an array
                      font: parseFontObj(ensureArray(element.font)[0]),
                      fontOptions: (element.fontOptions || [])
                          .map(parseFontObj)
                          .filter(isNotNullish),
                      maxLines:
                          (element.maxLines?.value && ensureInt(element.maxLines?.value)) || 1,
                      aspectRatio:
                          element.width && element.height
                              ? element.width / element.height
                              : undefined,
                  })) || []
    }

    /**
     * Compute personalized form parts based on configuration and product elements.
     * TODO: rearrange fields if the color field is the only field in a fieldset
     *
     * @returns Computed form parts, including nested fieldsets and fields.
     */
    const formParts = computed(() => {
        const baseStructure = designProject.value?.configurationCurrent.baseStructure || []

        // Count card and envelope surfaces
        const cardSurfaceCount = baseStructure.filter(
            (part) => !part.name.includes('Envelope')
        ).length
        const envelopeSurfaceCount = baseStructure.length - cardSurfaceCount

        const formPartArr = baseStructure.map((productPart) => {
            const formPartId: FormPartType = productPart.name.includes('Envelope')
                ? 'envelope'
                : 'card'
            const formPart = personalizeFormPartConfigs.find(
                (formPartConfig) => formPartConfig.id === formPartId
            )

            // Get surface name from surface id
            const surface = surfaceIds.find((surface) => surface.id === productPart.surfaceId)

            if (!formPart || !surface) return undefined

            // Conditionally format title based on surface count
            const surfaceCount = formPartId === 'envelope' ? envelopeSurfaceCount : cardSurfaceCount
            const title =
                surfaceCount > 1
                    ? `Personalize ${surface?.name} of ${capitalize(formPartId)}`
                    : `Personalize ${capitalize(formPartId)}`

            return {
                ...formPart,
                id: `${formPartId}_${changeCase.snakeCase(surface?.name)}`,
                title,
                fieldsets: formPart.fieldsets
                    .map((fieldset) => {
                        const path = changeCase.camelCase(`${formPart.id}_${fieldset.id}`)
                        return {
                            ...fieldset,
                            // Flatten mobile copy for existing call sites.
                            title: fieldset.mobile.title,
                            subtitle: fieldset.mobile.subtitle,
                            // Keep the desktop header payload available for the Fieldset component.
                            desktopHeader: fieldset.desktop,
                            // Start off in camelCase since that's how values will be
                            // transformed after coming back from the server
                            path,
                            fields: getFieldsForProductPart(
                                productPart,
                                { ...fieldset, path },
                                formFieldPlaceholders.value
                            ),
                        }
                    })
                    .filter((fieldset) => fieldset.fields.length > 0),
            }
        })

        // Helper to check if form part has image fields
        const hasImageField = (formPart: (typeof formPartArr)[number]) =>
            formPart?.fieldsets.some((fieldset) =>
                fieldset.fields.some((field) => field.type === 'image')
            ) ?? false

        // Helper to check if form part has non-image/non-color fields
        const hasOtherFields = (formPart: (typeof formPartArr)[number]) =>
            formPart?.fieldsets.some((fieldset) =>
                fieldset.fields.some((field) => field.type !== 'image' && field.type !== 'color')
            ) ?? false

        return (
            formPartArr
                .filter(isNotNullish)
                // Sort to ensure parts with image fields come first
                .sort((a, b) => {
                    const aHasImage = hasImageField(a)
                    const bHasImage = hasImageField(b)
                    return bHasImage ? 1 : aHasImage ? -1 : 0
                })
                // Process fieldsets: remove color field from non-first parts, and filter color fieldset from envelope parts that aren't first
                .map((formPart, index) => {
                    const isFirstPart = index === 0
                    const isEnvelopePart = formPart.id.startsWith('envelope')
                    const keepColorFields =
                        (isFirstPart || hasOtherFields(formPart)) && !hasImageField(formPart)

                    return {
                        ...formPart,
                        fieldsets: formPart.fieldsets
                            .map((fieldset) => ({
                                ...fieldset,
                                fields:
                                    // Remove color fieldset from envelope parts that aren't first
                                    fieldset.id === 'color' && isEnvelopePart && !isFirstPart
                                        ? []
                                        : // Remove color fields from non-first parts without other fields
                                          !keepColorFields && fieldset.id === 'color'
                                          ? fieldset.fields.filter((field) => field.id !== 'color')
                                          : fieldset.fields,
                            }))
                            .filter((fieldset) => fieldset.fields.length > 0),
                    }
                })
        )
    })

    /**
     * List of all field configs for the design project.
     * This is used for validation purposes.
     */
    const allFieldConfigs = computed(() => {
        return formParts.value
            .flatMap((formPart) => formPart.fieldsets)
            .flatMap((fieldset) => fieldset.fields)
    })

    /**
     * List of printing processes that are an option for this design project.
     */
    const printingProcessOptions = ref<Print[]>([])

    /**
     * All of the color options for the design project as Color objects.
     * Derived from the intersection of all of the color options for each PartElement
     * in the design project.
     */
    const highLevelColorOptions = ref<Color[]>([])

    /**
     * Adds a hex value to each color option related to the selected printing process.
     * Includes all colors (even those without hex for current process) so they can be
     * displayed with warnings. Falls back to the first available hex from processHex
     * if the current process doesn't have one. Uses a default gray hex if no hex is
     * available at all to ensure the color can be displayed.
     */
    const colorOptions = computed(() =>
        highLevelColorOptions.value
            .filter((color) => isNotNullish(color.cid))
            .map((color) => {
                const processKey = selectedPrintingProcess.value?.shortName.toLowerCase() as
                    | keyof typeof color.processHex
                    | undefined
                const preferredHex = processKey ? color.processHex?.[processKey] : undefined
                // Fallback to first available hex if current process doesn't have one
                const fallbackHex = color.processHex
                    ? Object.values(color.processHex).find((hex) => hex && hex !== '')
                    : undefined
                // Use default gray if no hex is available at all
                const hex = preferredHex || fallbackHex || 'cccccc'

                return {
                    ...color,
                    hex: hex as string,
                    cid: color.cid!.toString(),
                    name: color.name || hex || 'Unknown Color',
                }
            })
    )

    /**
     * Returns true if any selected colors are incompatible with the current printing process.
     * A color is incompatible if it doesn't have a hex value for the selected printing process.
     */
    const hasIncompatibleColors = computed(() => {
        if (!selectedPrintingProcess.value) return false

        const shortName = getPrintingProcessShortName(selectedPrintingProcess.value)
        if (!shortName) return false

        const processKey = shortName.toLowerCase()

        return selectedColorOptions.value.some((color) => {
            if (!color.processHex) return false
            const hex = (color.processHex as Record<string, string>)[processKey]
            return !hex || hex === ''
        })
    })

    /**
     * Checks if a specific color (by cid) is incompatible with the current printing process.
     * @param colorCid - The color cid to check
     * @returns True if the color is incompatible, false otherwise
     */
    const isColorIncompatible = (colorCid: string): boolean => {
        if (!selectedPrintingProcess.value || !colorCid) return false

        const color = highLevelColorOptions.value.find((c) => c.cid?.toString() === colorCid)
        if (!color || !color.processHex) return false

        const shortName = getPrintingProcessShortName(selectedPrintingProcess.value)
        if (!shortName) return false

        const processKey = shortName.toLowerCase()
        const hex = (color.processHex as Record<string, string>)[processKey]
        return !hex || hex === ''
    }

    /**
     * If two colors are active and the user removes a color: all elements will
     * be updated to the remaining Color.
     * If three or more colors are active, then the color index of each field
     * that has that index or greater is updated to be one less, with a minimum of 0.
     *
     * Once the new color indexes are calculated, updates the field colors and the
     * set of colors.
     */
    const onRemoveColor = (removedIndex: number) => {
        const newFieldColors = Object.fromEntries(
            Object.entries(formFieldColors.value).map(([fieldKey, colorIndex]) => {
                const newColorIndex =
                    colorIndex >= removedIndex ? Math.max(colorIndex - 1, 0) : colorIndex
                return [fieldKey, newColorIndex]
            })
        )

        formFieldColors.value = newFieldColors
    }

    const getFieldsetForField = (fieldId: string) =>
        formParts.value
            .map((formPart) => formPart.fieldsets)
            .flat()
            .find((fieldset) => fieldset.fields.find((field) => field.id === fieldId))

    /**
     * Checks if a PartElement supports a given printing process.
     * @param element - The PartElement to check
     * @param print - The printing process to check support for
     * @returns True if the element supports the printing process, false otherwise
     */
    const elementSupportsPrintingProcess = (element: PartElement, print: Print | null): boolean => {
        if (!print || !element.prints || element.prints.length === 0) {
            return false
        }

        return element.prints.some((supportedPrint) => supportedPrint.processId === print.processId)
    }

    /**
     * Returns a copy of the `configurationCurrent` object with the current form
     * changes inserted into it.
     */
    const getUpdatedConfigurationCurrent = () => {
        if (!designProject.value) return undefined
        const configurationCurrentCopy = JSON.parse(
            JSON.stringify(designProject.value.configurationCurrent)
        ) as ConfigurationTemplate

        configurationCurrentCopy.baseStructure.forEach((productPart) => {
            productPart.elements.forEach((element) => {
                // Use fieldKeyMap when available; fallback to getFieldKey so we never skip
                // elements (e.g. liner) due to case mismatch or map not yet populated
                const elementFieldKey = fieldKeyMap.value[element.name] ?? getFieldKey(element)
                if (!ELEMENT_TYPES_WITH_FIELDS.includes(element.type)) return

                const data = trimmedFormData.value[elementFieldKey]

                const elementFieldset = getFieldsetForField(elementFieldKey)
                const fieldsetPath = elementFieldset?.path
                const fieldsetId = elementFieldset?.id
                const requiresToggle = fieldsetId
                    ? fieldsetRequiresVisibilityToggle(fieldsetId)
                    : false

                // Determine if fieldset should be hidden:
                // - For fieldsets requiring toggle: hide unless explicitly set to 'show'
                // - For other fieldsets: only hide when explicitly set to 'hide'
                const fieldsetSetToHide = fieldsetPath
                    ? requiresToggle
                        ? fieldsetsHidden.value[fieldsetPath] !== 'show'
                        : fieldsetsHidden.value[fieldsetPath] === 'hide'
                    : false
                const fieldIsHidden = isFieldHidden(changeCase.camelCase(elementFieldKey))

                // Simplified logic: Determine if element should be removed based ONLY on current state
                // Don't check element.removed from previous state - it causes persistence issues
                // Priority: fieldset visibility > field hidden > data validation
                let shouldBeRemoved = false

                // Fieldset visibility takes highest priority - if fieldset is hidden, element must be removed
                if (fieldsetSetToHide) {
                    shouldBeRemoved = true
                } else if (fieldIsHidden) {
                    // Individual field hidden state
                    shouldBeRemoved = true
                } else {
                    // For non-text elements, check if data is null/undefined
                    // Text elements can be empty strings, so we don't remove them for that
                    if (element.type !== 'text' && data && isNullish(data.value)) {
                        shouldBeRemoved = true
                    }
                }

                // If fieldset is explicitly shown, element should NOT be removed
                // This ensures that when user clicks "Show Address", removed is cleared
                if (
                    fieldsetPath &&
                    requiresToggle &&
                    fieldsetsHidden.value[fieldsetPath] === 'show'
                ) {
                    shouldBeRemoved = false
                }

                // If user has selected a value for this element (liner, motif, etc.), never mark as removed.
                // This fixes the case where formFieldHidden was still true from "No Liner" but the user
                // then selected a liner — we must persist removed: false so the payload is correct.
                const hasValue =
                    data?.value != null &&
                    data.value !== '' &&
                    (typeof data.value !== 'object' || Object.keys(data.value).length > 0)
                if (hasValue) {
                    shouldBeRemoved = false
                    // Keep form state in sync so future saves don't rely only on this override
                    formFieldHidden.value[changeCase.camelCase(elementFieldKey)] = false
                }

                // Always set both removed and isOptionalAdded properties explicitly
                element.removed = shouldBeRemoved
                element.isOptionalAdded = !shouldBeRemoved

                // Only process data-dependent updates if data exists
                if (data) {
                    // Only set the printing process if the element supports it.
                    // This preserves elements that have specific printing process
                    // requirements (e.g., "Digital" printing for photos).
                    if (elementSupportsPrintingProcess(element, selectedPrintingProcess.value)) {
                        element.print = selectedPrintingProcess.value
                    }
                    const elementColor = selectedColorOptions.value[data.color]
                    if (elementColor) {
                        element.color = elementColor
                    }

                    // Handle element value - always update if data exists
                    // The removed flag controls visibility, but value should still be set
                    element.value = data.value

                    if (element.type === 'text') {
                        element.font = data.font
                    }
                }

                element.placeholder = formFieldPlaceholders.value[elementFieldKey]
            })
        })

        const highLevelOptions = configurationCurrentCopy.highLevelOptions || {
            colors: [],
            prints: [],
        }
        highLevelOptions.colors = highLevelColorOptions.value
        highLevelOptions.prints = printingProcessOptions.value
        configurationCurrentCopy.highLevelOptions = highLevelOptions

        const highLevelChoices = configurationCurrentCopy.highLevelChoices || {
            colors: [],
            print: null,
            additionalColorsAdded: false,
        }
        // NOTE: only handles single printing process atm
        highLevelChoices.colors = selectedColorOptions.value
        highLevelChoices.print = selectedPrintingProcess.value
        highLevelChoices.additionalColorsAdded = additionalColorsAdded.value
        highLevelChoices.fieldsetsHidden = fieldsetsHidden.value
        highLevelChoices.formFieldHidden = formFieldHidden.value
        highLevelChoices.stashedFieldValues = stashedFieldValues.value
        configurationCurrentCopy.highLevelChoices = highLevelChoices

        return configurationCurrentCopy
    }

    /**
     * A copy of the configurationCurrent
     * structure that has been updated with the latest form data, then
     * saves that change to the designProject store (triggering a PATCH)
     * to the API to persist the changes
     *
     * Updates the design project configuration which should trigger automatic re-rendering
     */
    const saveDesignProject = () => {
        try {
            const updatedConfigurationCurrent = getUpdatedConfigurationCurrent()

            if (!updatedConfigurationCurrent) {
                return
            }

            updateDesignProjectConfigurationCurrent(updatedConfigurationCurrent)

            // Trigger pricing recalculation when configuration changes
            // Pricing is now handled by productPrice store which watches designProject changes
            // No manual recalculation needed
        } catch (error) {
            // Handle errors gracefully to prevent UI breaking
            createError(error as Error)
        }
    }

    /**
     * Debounced version of `saveDesignProject` for text input changes (500ms delay).
     */
    const { debouncedFn: debouncedSaveDesignProjectText, cancel: cancelTextDebounce } = debounce(
        saveDesignProject,
        500,
        'saveDesignProject'
    )

    /**
     * Debounced version of `saveDesignProject` for non-text changes (100ms delay).
     * Uses shared key 'saveDesignProject' so it cancels text saves and vice versa.
     */
    const { debouncedFn: debouncedSaveDesignProjectNonText, cancel: cancelNonTextDebounce } =
        debounce(saveDesignProject, 100, 'saveDesignProject')

    /**
     * Watches for the design project to be initially loaded,
     * then iterates over all of the product part elements that
     * have fields in order to copy over their initial values
     * from the configuration template.
     *
     * NOTE: If we want to set different initial values or
     * prefill data with some related to the user, this is
     * the place to do so.
     */
    // Track the current design project ID to detect changes
    const currentDesignProjectId = ref<string | null>(null)

    /**
     * Flag to track when we're resetting the form state to avoid triggering saves
     * during initialization/reset operations
     */
    const isResettingForm = ref(false)

    /**
     * Reset all personalize form state when switching design projects
     * This ensures clean state for each new project
     */
    const resetPersonalizeFormState = () => {
        // Cancel any pending debounced saves to prevent stale saves
        cancelTextDebounce()
        cancelNonTextDebounce()

        isResettingForm.value = true
        selectedPrintingProcess.value = null
        selectedColors.value = []
        fieldKeyMap.value = {}
        formFieldValues.value = {}
        formFieldColors.value = {}
        formFieldFonts.value = {}
        formFieldPlaceholders.value = {}
        formFieldHidden.value = {}
        stashedFieldValues.value = {}
        fieldsetsHidden.value = {}
        additionalColorsAdded.value = false

        // Setup the form
        if (!allDesignProjectElements.value.length) {
            isResettingForm.value = false
            return
        }

        // Always initialize high_level_options and high_level_choices using the utility
        // This ensures they're derived from the base structure and handles edge cases
        // where existing projects have high_level_options but missing/empty prints
        if (designProject.value?.configurationCurrent) {
            initializeHighLevelData(designProject.value.configurationCurrent)
        }

        // Re-read the initialized values (may have been updated if prints were missing)
        const { highLevelOptions, highLevelChoices } =
            designProject.value?.configurationCurrent || {}

        // Track if we need to save the default return address setting to the database
        let shouldSaveReturnAddressDefault = false

        if (highLevelOptions && highLevelChoices) {
            selectedColors.value = highLevelChoices.colors.map(
                (color) => color.cid?.toString() || ''
            )

            // Sort printing processes to match preferred order
            printingProcessOptions.value = sortPrintingProcessesByPreferred(highLevelOptions.prints)
            // If there's only one printing process option, auto-select it
            // Otherwise, use the saved choice (which may be null if user hasn't selected yet)
            if (printingProcessOptions.value.length === 1) {
                selectedPrintingProcess.value = printingProcessOptions.value[0] || null
                // Save the auto-selected process after form reset completes
                nextTick(() => {
                    saveDesignProject()
                })
            } else {
                selectedPrintingProcess.value = highLevelChoices.print
            }
            highLevelColorOptions.value = highLevelOptions.colors
            additionalColorsAdded.value = highLevelChoices.additionalColorsAdded || false
            fieldsetsHidden.value = highLevelChoices.fieldsetsHidden || {}
            formFieldHidden.value = highLevelChoices.formFieldHidden || {}

            // Set default for return address fieldset to 'show' for all envelope parts that have it
            const baseStructure = designProject.value?.configurationCurrent.baseStructure || []

            // FALLBACK: Sync formFieldHidden from base_structure.removed flags
            // This ensures consistency if formFieldHidden is out of sync with base_structure
            // This can happen if data was saved incorrectly in a previous version
            baseStructure.forEach((part) => {
                part.elements?.forEach((element) => {
                    if (ELEMENT_TYPES_WITH_FIELDS.includes(element.type)) {
                        const fieldKey = getFieldKey(element)
                        const fieldKeyCamelCase = changeCase.camelCase(fieldKey)

                        // If element is marked as removed in base_structure but not in formFieldHidden,
                        // sync formFieldHidden to match base_structure (source of truth)
                        if (element.removed === true && !formFieldHidden.value[fieldKeyCamelCase]) {
                            formFieldHidden.value[fieldKeyCamelCase] = true
                        }
                        // If element is NOT removed in base_structure but is marked as hidden,
                        // sync formFieldHidden to match base_structure (source of truth)
                        else if (
                            element.removed === false &&
                            formFieldHidden.value[fieldKeyCamelCase] === true
                        ) {
                            formFieldHidden.value[fieldKeyCamelCase] = false
                        }
                    }
                })
            })

            baseStructure.forEach((part) => {
                // Check if part name contains "envelope" (case-insensitive)
                const partNameLower = part.name.toLowerCase()
                if (!partNameLower.includes('envelope')) {
                    return
                }

                // Check if this part has a return address element
                const hasReturnAddressElement = part.elements?.some(
                    (element) => getFieldKey(element) === 'return_address'
                )

                if (hasReturnAddressElement) {
                    // Construct the fieldset path: camelCase(partName)ReturnAddress
                    // This matches the pattern used in formParts where
                    // path = changeCase.camelCase(`${formPart.id}_${fieldset.id}`)
                    const partNameCamel = changeCase.camelCase(part.name)
                    const fieldsetPath = `${partNameCamel}ReturnAddress`

                    if (!fieldsetsHidden.value[fieldsetPath]) {
                        fieldsetsHidden.value[fieldsetPath] = 'show'
                        shouldSaveReturnAddressDefault = true
                    }
                }
            })
            // Handle migration from old structure (formFieldStashedValues + stashedSelections) to new unified structure
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const choicesAny = highLevelChoices as any
            const oldStashedValues = choicesAny.formFieldStashedValues || {}
            const oldStashedSelections = choicesAny.stashedSelections || {}
            stashedFieldValues.value = {
                ...oldStashedValues,
                ...oldStashedSelections,
                ...(highLevelChoices.stashedFieldValues || {}),
            }

            // Derive selected colors from current element colors in the configuration
            // This is separate from initialization and uses the actual colors assigned to elements
            // Merge with saved colors from highLevelChoices to preserve colors not yet assigned to elements
            const mainColors = (designProject.value?.configurationCurrent?.baseStructure || [])
                .map((b) => b?.elements.map((e) => e.color))
                .filter((colors) => !!colors)
                .flat()
            if (mainColors) {
                // Use the list of selected colors to get a list of the selected cids,
                // only if they're also present in the list of `colorOptions`
                const elementColorList = ensureArray(mainColors)
                    .map((colorObj) =>
                        colorOptions.value.find((color) => color.cid === colorObj?.cid)
                    )
                    .map((colorObj) => colorObj?.cid?.toString())
                    .filter(isNotNullish)
                    .filter((color, i, arr) => arr.indexOf(color) === i)
                // Merge element-derived colors with saved colors, preserving all unique colors
                if (elementColorList.length > 0) {
                    const mergedColors = [
                        ...new Set([...selectedColors.value, ...elementColorList]),
                    ]
                    selectedColors.value = mergedColors
                }
            }
        }

        const elementsWithFields = allDesignProjectElements.value.filter((e) =>
            ELEMENT_TYPES_WITH_FIELDS.includes(e.type)
        )
        // Iterate over the PartElement objects that have corresponding fields and set up
        // their initial values and color selections
        elementsWithFields.forEach((element) => {
            const fieldKey = getFieldKey(element)
            fieldKeyMap.value[element.name] = fieldKey

            // If element is removed, set form value to undefined so UI shows "No item" as selected
            if (element.removed === true) {
                formFieldValues.value[fieldKey] = undefined
            } else if (element.type === 'text' && !element.placeholder) {
                // For text fields, clear values during initialization, leaving only placeholder text
                // Placeholder text is set during initialization, so this should only happen once
                formFieldValues.value[fieldKey] = undefined
            } else {
                // Keep configuration values as provided (no trim on hydrate for text).
                // Submit validation treats whitespace-only text as empty (`personalizeFormSchema`).
                formFieldValues.value[fieldKey] = element.value
            }
            // TODO: determine if non-matching colors are an issue

            if (element.color) {
                const color = selectedColorOptions.value.findIndex(
                    (color) => color.cid === element.color?.cid
                )
                formFieldColors.value[fieldKey] = color > -1 ? color : 0
            }

            if (element.type === 'text') {
                // Always use original configuration value as placeholder, never as field value
                // Handle edge cases: empty values, null values, undefined values
                const configValue = element.value as string
                const placeholderValue = element.placeholder || configValue || ''
                formFieldPlaceholders.value[fieldKey] = placeholderValue
                formFieldFonts.value[fieldKey] = parseFontObj(ensureArray(element.font)[0])
            }
        })

        // Reset the flag after form setup is complete
        nextTick(() => {
            // Reset the flag after form setup is complete
            isResettingForm.value = false

            // If we set a default for return address, save it to the database
            if (shouldSaveReturnAddressDefault) {
                saveDesignProject()
            }
        })
    }

    // Watch for design project changes and reset form state
    watch(
        () => designProject.value?.id,
        (newId) => {
            if (newId && newId !== currentDesignProjectId.value) {
                // Design project changed, reset form state
                resetPersonalizeFormState()
            }
            currentDesignProjectId.value = newId || null
        },
        { immediate: true }
    )

    watch(selectedColorOptions, (newColorOptions, oldColorOptions) => {
        // If this is the first update, ignore it
        // TODO: this actually may want to remove this condition
        // to save the select colors as their own prop
        if (oldColorOptions.length === 0) return

        // Skip save if we're currently resetting the form state
        if (isResettingForm.value) return

        // When colors are removed, make sure formFieldColors is updated
        if (newColorOptions.length < oldColorOptions.length) {
            const newColorCids = new Set(newColorOptions.map((c) => c.cid))
            const removedIndex = oldColorOptions.findIndex(
                (colorOption) => !newColorCids.has(colorOption.cid)
            )
            onRemoveColor(removedIndex)
        }

        // If there are no changes, don't save anything (avoid endless loop)
        if (newColorOptions.every((colorOption, i) => oldColorOptions[i]?.cid === colorOption.cid))
            return
        debouncedSaveDesignProjectNonText()
    })

    watch(
        formData,
        (newData, oldData) => {
            // If this is the first update, ignore it
            if (Object.keys(oldData).length === 0) return

            // Skip save if we're currently resetting the form state
            if (isResettingForm.value) return

            // Determine if the changes are text-related
            const changeType = detectFormFieldValuesChangeType(newData, oldData)

            // Use appropriate debounced function based on change type
            if (changeType === 'text') {
                debouncedSaveDesignProjectText()
            } else {
                debouncedSaveDesignProjectNonText()
            }
        },
        { deep: true }
    )

    watch(additionalColorsAdded, (newVal, oldVal) => {
        if (!allDesignProjectElements.value.length) {
            return
        }

        // Skip save if we're currently resetting the form state
        if (isResettingForm.value) return

        // If the addtionalColorsAdded has been flipped to true,
        // save that change to the DesignProject
        if (newVal && oldVal === false) debouncedSaveDesignProjectNonText()
    })

    watch(
        fieldsetsHidden,
        () => {
            // Skip save if we're currently resetting the form state
            if (isResettingForm.value) return

            debouncedSaveDesignProjectNonText()
        },
        { deep: true }
    )

    watch(
        formFieldHidden,
        () => {
            // Skip save if we're currently resetting the form state
            if (isResettingForm.value) return

            debouncedSaveDesignProjectNonText()
        },
        { deep: true }
    )

    watch(
        stashedFieldValues,
        () => {
            // Skip save if we're currently resetting the form state
            if (isResettingForm.value) return

            debouncedSaveDesignProjectNonText()
        },
        { deep: true }
    )

    return {
        additionalColorsAdded,
        allDesignProjectElements,
        allFieldConfigs,
        clearAllStashedValues,
        clearStashedValue,
        colorOptions,
        fieldKeyMap,
        fieldsetRequiresVisibilityToggle,
        fieldsetsHidden,
        formData,
        formFieldColors,
        formFieldHidden,
        formFieldValues,
        formFieldFonts,
        stashedFieldValues,
        formParts,
        formState,
        getUpdatedConfigurationCurrent,
        detectFormFieldValuesChangeType,
        getChangeType,
        getFieldTypeByKey,
        hasIncompatibleColors,
        isColorIncompatible,
        isFieldHidden,
        isTextField,
        printingProcessOptions,
        onRemoveColor,
        resetPersonalizeFormState,
        restoreFieldValue,
        restoreSelection,
        saveDesignProject,
        stashFieldValue,
        stashSelection,
        debouncedSaveDesignProjectText,
        debouncedSaveDesignProjectNonText,
        cancelTextDebounce,
        cancelNonTextDebounce,
        selectedColors,
        selectedColorOptions,
        selectedPrintingProcess,
        setFieldHidden,
        setSelectedPrintingProcess,
    }
})
